import { useMutation, useQueryClient } from "@tanstack/react-query";

import useGetAccessToken from "components/Login/useGetAccessToken";
import type { ChatThread } from "types/ChatThread";
import { postThread } from "../methods/postThread";
import { MESSAGES_WRITE_OPTIONS } from "./messagesWriteOptions";

/**
 * Creates a new chat thread and prepends it to the cached threads list.
 */
export default function usePostThreadMutation() {
  const getAccessToken = useGetAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (thread: ChatThread) => postThread(thread, getAccessToken),
    ...MESSAGES_WRITE_OPTIONS,
    onSuccess: async (data) => {
      // Cancel any in-flight GET /threads first. A fetch issued before this
      // create committed server-side would otherwise resolve after the insert
      // below and overwrite the new thread with a pre-commit server snapshot,
      // making the Chat route render a 404 for a thread that exists (ADR 060).
      // `exact: true` so we cancel only the threads-list query, not the
      // per-thread messages queries that share the ["cc-db-api","threads",...]
      // key prefix.
      await queryClient.cancelQueries({
        queryKey: ["cc-db-api", "threads"],
        exact: true,
      });

      // Insert the new thread into the cached list directly. Avoiding
      // invalidate so a newly-created thread doesn't trigger an extra
      // GET /threads while the user is mid-flow.
      let hadPrev = false;
      queryClient.setQueryData<ChatThread[]>(
        ["cc-db-api", "threads"],
        (prev) => {
          hadPrev = prev !== undefined;
          return prev ? [data, ...prev] : [data];
        }
      );

      // Cold-load case: if we cancelled the first-ever GET /threads (no prior
      // data), the insert above left a single-element list, so the sidebar
      // would show only the new chat until the next focus refetch. Refetch now
      // to restore the full history. Safe from re-racing the 404: the POST has
      // already committed, so the response includes the new thread. Skipped on
      // the common path (prev present) to keep the create at zero extra network
      // cost. See ADR 060.
      if (!hadPrev) {
        queryClient.invalidateQueries({
          queryKey: ["cc-db-api", "threads"],
          exact: true,
        });
      }
    },
  });
}
